/**
 * React Native Stubs for Web
 *
 * These are placeholder components that mirror React Native's API
 * but render using standard HTML elements.
 *
 * TODO: Replace these with proper web implementations
 *
 * @module @kidslabs/module-flash-sale/utils/react-native-stubs
 */

import React, { forwardRef, type CSSProperties, type ReactNode } from 'react';

// ============================================================================
// Basic Components
// ============================================================================

interface ViewProps {
    children?: ReactNode;
    style?: CSSProperties | CSSProperties[];
    className?: string;
    testID?: string;
}

export const View = forwardRef<HTMLDivElement, ViewProps>(({ children, style, className, testID, ...props }, ref) => {
    const flatStyle = Array.isArray(style) ? Object.assign({}, ...style) : style;
    return (
        <div ref={ref} style={flatStyle} className={className} data-testid={testID} {...props}>
            {children}
        </div>
    );
});
View.displayName = 'View';

interface TextProps {
    children?: ReactNode;
    style?: CSSProperties | CSSProperties[];
    className?: string;
    testID?: string;
    numberOfLines?: number;
}

export const Text = forwardRef<HTMLSpanElement, TextProps>(
    ({ children, style, className, testID, numberOfLines, ...props }, ref) => {
        const flatStyle = Array.isArray(style) ? Object.assign({}, ...style) : style;
        const webStyle: CSSProperties = {
            ...flatStyle,
            ...(numberOfLines
                ? {
                      display: '-webkit-box',
                      WebkitLineClamp: numberOfLines,
                      WebkitBoxOrient: 'vertical' as const,
                      overflow: 'hidden',
                  }
                : {}),
        };
        return (
            <span ref={ref} style={webStyle} className={className} data-testid={testID} {...props}>
                {children}
            </span>
        );
    }
);
Text.displayName = 'Text';

// ============================================================================
// Touchable Components
// ============================================================================

interface PressableProps {
    children?: ReactNode;
    onPress?: () => void;
    disabled?: boolean;
    style?: CSSProperties | CSSProperties[] | ((state: { pressed: boolean }) => CSSProperties);
    className?: string;
    testID?: string;
}

export const Pressable = forwardRef<HTMLButtonElement, PressableProps>(
    ({ children, onPress, disabled, style, className, testID, ...props }, ref) => {
        const [pressed, setPressed] = React.useState(false);
        const computedStyle = typeof style === 'function' ? style({ pressed }) : style;
        const flatStyle = Array.isArray(computedStyle) ? Object.assign({}, ...computedStyle) : computedStyle;
        return (
            <button
                ref={ref}
                type="button"
                onClick={onPress}
                disabled={disabled}
                style={{
                    ...flatStyle,
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                }}
                className={className}
                data-testid={testID}
                onMouseDown={() => setPressed(true)}
                onMouseUp={() => setPressed(false)}
                onMouseLeave={() => setPressed(false)}
                {...props}
            >
                {children}
            </button>
        );
    }
);
Pressable.displayName = 'Pressable';

interface TouchableOpacityProps {
    children?: ReactNode;
    onPress?: () => void;
    disabled?: boolean;
    activeOpacity?: number;
    style?: CSSProperties | CSSProperties[];
    className?: string;
    testID?: string;
}

export const TouchableOpacity = forwardRef<HTMLButtonElement, TouchableOpacityProps>(
    ({ children, onPress, disabled, activeOpacity = 0.7, style, className, testID, ...props }, ref) => {
        const [pressed, setPressed] = React.useState(false);
        const flatStyle = Array.isArray(style) ? Object.assign({}, ...style) : style;
        return (
            <button
                ref={ref}
                type="button"
                onClick={onPress}
                disabled={disabled}
                style={{
                    ...flatStyle,
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: pressed ? activeOpacity : 1,
                    transition: 'opacity 0.1s',
                }}
                className={className}
                data-testid={testID}
                onMouseDown={() => setPressed(true)}
                onMouseUp={() => setPressed(false)}
                onMouseLeave={() => setPressed(false)}
                {...props}
            >
                {children}
            </button>
        );
    }
);
TouchableOpacity.displayName = 'TouchableOpacity';

// ============================================================================
// List Components
// ============================================================================

interface FlatListProps<T> {
    data: T[];
    renderItem: (info: { item: T; index: number }) => ReactNode;
    keyExtractor?: (item: T, index: number) => string;
    horizontal?: boolean;
    numColumns?: number;
    style?: CSSProperties | CSSProperties[];
    contentContainerStyle?: CSSProperties | CSSProperties[];
    columnWrapperStyle?: CSSProperties;
    ItemSeparatorComponent?: React.ComponentType<unknown>;
    ListHeaderComponent?: React.ComponentType<unknown> | ReactNode;
    ListFooterComponent?: React.ComponentType<unknown> | ReactNode;
    ListEmptyComponent?: React.ComponentType<unknown> | ReactNode;
    refreshControl?: ReactNode;
    showsHorizontalScrollIndicator?: boolean;
    showsVerticalScrollIndicator?: boolean;
    scrollEventThrottle?: number;
    snapToInterval?: number;
    snapToAlignment?: 'start' | 'center' | 'end';
    decelerationRate?: number | 'fast' | 'normal';
    windowSize?: number;
    onEndReached?: () => void;
    onEndReachedThreshold?: number;
    refreshing?: boolean;
    onRefresh?: () => void;
    initialNumToRender?: number;
    maxToRenderPerBatch?: number;
    updateCellsBatchingPeriod?: number;
    removeClippedSubviews?: boolean;
    getItemLayout?: (data: T[] | null, index: number) => { length: number; offset: number; index: number };
    testID?: string;
    className?: string;
}

export function FlatList<T>({
    data,
    renderItem,
    keyExtractor,
    horizontal = false,
    numColumns = 1,
    style,
    contentContainerStyle,
    ItemSeparatorComponent,
    ListHeaderComponent,
    ListFooterComponent,
    ListEmptyComponent,
    refreshControl,
    showsHorizontalScrollIndicator,
    showsVerticalScrollIndicator,
    testID,
    className,
}: FlatListProps<T>) {
    const flatStyle = Array.isArray(style) ? Object.assign({}, ...style) : style;
    const flatContentStyle = Array.isArray(contentContainerStyle)
        ? Object.assign({}, ...contentContainerStyle)
        : contentContainerStyle;

    const containerStyle: CSSProperties = {
        ...flatStyle,
        overflow: 'auto',
        ...(showsHorizontalScrollIndicator === false ? { overflowX: 'hidden' } : {}),
        ...(showsVerticalScrollIndicator === false ? { overflowY: 'hidden' } : {}),
    };

    const getDisplay = () => {
        if (horizontal) return 'flex';
        if (numColumns > 1) return 'grid';
        return 'flex';
    };

    const contentStyle: CSSProperties = {
        ...flatContentStyle,
        display: getDisplay(),
        flexDirection: horizontal ? 'row' : 'column',
        ...(numColumns > 1
            ? {
                  gridTemplateColumns: `repeat(${numColumns}, 1fr)`,
              }
            : {}),
    };

    if (!data || data.length === 0) {
        return (
            <div style={containerStyle} className={className} data-testid={testID}>
                {refreshControl}
                {typeof ListHeaderComponent === 'function' ? <ListHeaderComponent /> : ListHeaderComponent}
                {typeof ListEmptyComponent === 'function' ? <ListEmptyComponent /> : ListEmptyComponent}
                {typeof ListFooterComponent === 'function' ? <ListFooterComponent /> : ListFooterComponent}
            </div>
        );
    }

    return (
        <div style={containerStyle} className={className} data-testid={testID}>
            {refreshControl}
            {typeof ListHeaderComponent === 'function' ? <ListHeaderComponent /> : ListHeaderComponent}
            <div style={contentStyle}>
                {data.map((item, index) => (
                    <React.Fragment key={keyExtractor ? keyExtractor(item, index) : index}>
                        {renderItem({ item, index })}
                        {ItemSeparatorComponent && index < data.length - 1 && <ItemSeparatorComponent />}
                    </React.Fragment>
                ))}
            </div>
            {typeof ListFooterComponent === 'function' ? <ListFooterComponent /> : ListFooterComponent}
        </div>
    );
}

// ============================================================================
// Other Components
// ============================================================================

interface RefreshControlProps {
    refreshing: boolean;
    onRefresh: () => void;
    colors?: string[];
    tintColor?: string;
}

export const RefreshControl: React.FC<RefreshControlProps> = ({ refreshing }) => {
    if (!refreshing) return null;
    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'center',
                padding: '16px',
            }}
        >
            <span>Loading...</span>
        </div>
    );
};

interface ImageProps {
    source: { uri: string } | number;
    style?: CSSProperties | CSSProperties[];
    resizeMode?: 'cover' | 'contain' | 'stretch' | 'center';
    testID?: string;
    alt?: string;
}

export const Image: React.FC<ImageProps> = ({ source, style, resizeMode = 'cover', testID, alt = '' }) => {
    const flatStyle = Array.isArray(style) ? Object.assign({}, ...style) : style;
    const uri = typeof source === 'object' ? source.uri : '';
    return (
        <img
            src={uri}
            alt={alt}
            style={{
                ...flatStyle,
                objectFit: resizeMode,
            }}
            data-testid={testID}
        />
    );
};

// ============================================================================
// Type Exports (for ListRenderItem, etc.)
// ============================================================================

export type ListRenderItem<T> = (info: { item: T; index: number }) => ReactNode;
export type ViewStyle = CSSProperties;
export type TextStyle = CSSProperties;
export type ImageStyle = CSSProperties;

// ============================================================================
// StyleSheet stub
// ============================================================================

export const StyleSheet = {
    create: <T extends Record<string, CSSProperties>>(styles: T): T => styles,
    flatten: (style: CSSProperties | CSSProperties[] | undefined): CSSProperties => {
        if (!style) return {};
        if (Array.isArray(style)) return Object.assign({}, ...style);
        return style;
    },
    absoluteFillObject: {
        position: 'absolute' as const,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
    },
};

// ============================================================================
// Animated Stub for Web
// ============================================================================

interface AnimatedViewProps {
    children?: ReactNode;
    style?: CSSProperties | CSSProperties[];
    testID?: string;
}

const AnimatedView = forwardRef<HTMLDivElement, AnimatedViewProps>(({ children, style, testID, ...props }, ref) => {
    const flatStyle = Array.isArray(style) ? Object.assign({}, ...style) : style;
    return (
        <div ref={ref} style={{ transition: 'all 0.3s ease', ...flatStyle }} data-testid={testID} {...props}>
            {children}
        </div>
    );
});
AnimatedView.displayName = 'AnimatedView';

export const Animated = {
    View: AnimatedView,
    Text: Text,
    Image: Image,
    ScrollView: View,
    FlatList: FlatList,
    createAnimatedComponent: <T extends React.ComponentType<unknown>>(component: T) => component,
    timing: () => ({ start: (callback?: () => void) => callback?.() }),
    spring: () => ({ start: (callback?: () => void) => callback?.() }),
    Value: class AnimatedValue {
        _value: number;
        constructor(value: number) {
            this._value = value;
        }
        setValue(value: number) {
            this._value = value;
        }
        interpolate(_config: { inputRange: number[]; outputRange: (number | string)[] }) {
            return this._value;
        }
    },
    parallel: (animations: unknown[]) => ({
        start: (callback?: () => void) => {
            void animations;
            callback?.();
        },
    }),
    sequence: (animations: unknown[]) => ({
        start: (callback?: () => void) => {
            void animations;
            callback?.();
        },
    }),
    event: () => null,
};

// ============================================================================
// SectionList Component
// ============================================================================

export interface SectionListData<T> {
    title?: string;
    data: T[];
    [key: string]: unknown;
}

export interface SectionListRenderItemInfo<T> {
    item: T;
    index: number;
    section: SectionListData<T>;
}

export type SectionListRenderItem<T> = (info: SectionListRenderItemInfo<T>) => ReactNode;

export interface SectionListProps<T> {
    sections: SectionListData<T>[];
    renderItem: SectionListRenderItem<T>;
    renderSectionHeader?: (info: { section: SectionListData<T> }) => ReactNode;
    renderSectionFooter?: (info: { section: SectionListData<T> }) => ReactNode;
    keyExtractor?: (item: T, index: number) => string;
    stickySectionHeadersEnabled?: boolean;
    style?: CSSProperties | CSSProperties[];
    contentContainerStyle?: CSSProperties | CSSProperties[];
    ItemSeparatorComponent?: React.ComponentType<unknown>;
    SectionSeparatorComponent?: React.ComponentType<unknown>;
    ListHeaderComponent?: React.ComponentType<unknown> | ReactNode;
    ListFooterComponent?: React.ComponentType<unknown> | ReactNode;
    ListEmptyComponent?: React.ComponentType<unknown> | ReactNode;
    showsVerticalScrollIndicator?: boolean;
    testID?: string;
    className?: string;
    // Additional RN compat props
    removeClippedSubviews?: boolean;
    maxToRenderPerBatch?: number;
    windowSize?: number;
    initialNumToRender?: number;
    [key: string]: unknown; // Allow additional props
}

export function SectionList<T>({
    sections,
    renderItem,
    renderSectionHeader,
    renderSectionFooter,
    keyExtractor,
    stickySectionHeadersEnabled = true,
    style,
    contentContainerStyle,
    ItemSeparatorComponent,
    SectionSeparatorComponent,
    ListHeaderComponent,
    ListFooterComponent,
    ListEmptyComponent,
    showsVerticalScrollIndicator,
    testID,
    className,
}: SectionListProps<T>) {
    const flatStyle = Array.isArray(style) ? Object.assign({}, ...style) : style;
    const flatContentStyle = Array.isArray(contentContainerStyle)
        ? Object.assign({}, ...contentContainerStyle)
        : contentContainerStyle;

    const containerStyle: CSSProperties = {
        ...flatStyle,
        overflow: 'auto',
        ...(showsVerticalScrollIndicator === false ? { overflowY: 'hidden' } : {}),
    };

    const isEmpty = !sections || sections.length === 0 || sections.every(s => !s.data || s.data.length === 0);

    if (isEmpty) {
        return (
            <div style={containerStyle} className={className} data-testid={testID}>
                {typeof ListHeaderComponent === 'function' ? <ListHeaderComponent /> : ListHeaderComponent}
                {typeof ListEmptyComponent === 'function' ? <ListEmptyComponent /> : ListEmptyComponent}
                {typeof ListFooterComponent === 'function' ? <ListFooterComponent /> : ListFooterComponent}
            </div>
        );
    }

    return (
        <div style={containerStyle} className={className} data-testid={testID}>
            {typeof ListHeaderComponent === 'function' ? <ListHeaderComponent /> : ListHeaderComponent}
            <div style={flatContentStyle}>
                {sections.map((section, sectionIndex) => (
                    <div key={`section-${sectionIndex}`}>
                        {renderSectionHeader && (
                            <div
                                style={
                                    stickySectionHeadersEnabled
                                        ? { position: 'sticky', top: 0, zIndex: 10, backgroundColor: 'inherit' }
                                        : {}
                                }
                            >
                                {renderSectionHeader({ section })}
                            </div>
                        )}
                        {section.data.map((item, itemIndex) => (
                            <React.Fragment
                                key={keyExtractor ? keyExtractor(item, itemIndex) : `${sectionIndex}-${itemIndex}`}
                            >
                                {renderItem({ item, index: itemIndex, section })}
                                {ItemSeparatorComponent && itemIndex < section.data.length - 1 && (
                                    <ItemSeparatorComponent />
                                )}
                            </React.Fragment>
                        ))}
                        {renderSectionFooter && renderSectionFooter({ section })}
                        {SectionSeparatorComponent && sectionIndex < sections.length - 1 && (
                            <SectionSeparatorComponent />
                        )}
                    </div>
                ))}
            </div>
            {typeof ListFooterComponent === 'function' ? <ListFooterComponent /> : ListFooterComponent}
        </div>
    );
}

// ============================================================================
// UI Core Stubs - Components not yet in UI Core
// ============================================================================

/**
 * PromotionalItemCard stub
 * @see mobile: ui-react-native-core PromotionalItemCard component
 *
 * TODO: Add to @kidslabs/mario-nextjs-react-ui-core
 */
export interface PromotionalItemCardProps {
    title?: string;
    subtitle?: string;
    imageUrl?: string;
    price?: number;
    originalPrice?: number;
    quantity?: number;
    badge?: string;
    onClick?: () => void;
    testID?: string;
    style?: CSSProperties;
    className?: string;
}

export const PromotionalItemCard: React.FC<PromotionalItemCardProps> = ({
    title,
    subtitle,
    imageUrl,
    price,
    originalPrice,
    quantity,
    badge,
    onClick,
    testID,
    style,
    className,
}) => {
    return (
        <div
            data-testid={testID}
            onClick={onClick}
            style={{
                display: 'flex',
                alignItems: 'center',
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid #e0e0e0',
                cursor: onClick ? 'pointer' : 'default',
                ...style,
            }}
            className={className}
        >
            {imageUrl && (
                <img
                    src={imageUrl}
                    alt={title}
                    style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 4, marginRight: 12 }}
                />
            )}
            <div style={{ flex: 1 }}>
                {badge && (
                    <span
                        style={{
                            backgroundColor: '#ff6b6b',
                            color: 'white',
                            fontSize: 10,
                            padding: '2px 6px',
                            borderRadius: 4,
                            marginBottom: 4,
                            display: 'inline-block',
                        }}
                    >
                        {badge}
                    </span>
                )}
                {title && <div style={{ fontWeight: 600, fontSize: 14 }}>{title}</div>}
                {subtitle && <div style={{ fontSize: 12, color: '#666' }}>{subtitle}</div>}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                    {price !== undefined && (
                        <span style={{ fontWeight: 600, color: '#e53935' }}>{price.toLocaleString()}đ</span>
                    )}
                    {originalPrice !== undefined && originalPrice > (price ?? 0) && (
                        <span style={{ textDecoration: 'line-through', color: '#999', fontSize: 12 }}>
                            {originalPrice.toLocaleString()}đ
                        </span>
                    )}
                    {quantity !== undefined && quantity > 1 && (
                        <span style={{ fontSize: 12, color: '#666' }}>x{quantity}</span>
                    )}
                </div>
            </div>
        </div>
    );
};

/**
 * CallToAction stub
 * @see mobile: ui-react-native-core CallToAction component
 *
 * TODO: Add to @kidslabs/mario-nextjs-react-ui-core
 */
export interface CallToActionProps {
    title?: string;
    subtitle?: string;
    icon?: React.ReactNode;
    onClick?: () => void;
    variant?: 'primary' | 'secondary' | 'outline';
    testID?: string;
    style?: CSSProperties;
    className?: string;
}

export const CallToAction: React.FC<CallToActionProps> = ({
    title,
    subtitle,
    icon,
    onClick,
    variant = 'primary',
    testID,
    style,
    className,
}) => {
    const variantStyles: Record<string, CSSProperties> = {
        primary: {
            backgroundColor: '#21409A',
            color: 'white',
            border: 'none',
        },
        secondary: {
            backgroundColor: '#f5f5f5',
            color: '#333',
            border: 'none',
        },
        outline: {
            backgroundColor: 'transparent',
            color: '#21409A',
            border: '1px solid #21409A',
        },
    };

    return (
        <button
            type="button"
            data-testid={testID}
            onClick={onClick}
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '12px 24px',
                borderRadius: 8,
                cursor: 'pointer',
                gap: 8,
                ...variantStyles[variant],
                ...style,
            }}
            className={className}
        >
            {icon}
            <div style={{ textAlign: 'center' }}>
                {title && <div style={{ fontWeight: 600, fontSize: 14 }}>{title}</div>}
                {subtitle && <div style={{ fontSize: 12, opacity: 0.8 }}>{subtitle}</div>}
            </div>
        </button>
    );
};
